import { useField } from "formik";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { generateId, getValueByKey } from "../../../helper/helpers";
import CustomDropdown from "../common/dropdown";
import DropdownMenu from "../common/dropdownMenu";
import { MultipleSelectIcon } from "../common/icon";
import SearchBox from "../common/searchBox";
import Tags from "../common/tags";
import { filterOptions, sorter } from "../helpers";
import "../styles.scss";

const MultipleSelect = (props) => {
  const {
    allowClear = true,
    customClass,
    data: passedData = [],
    defaultValue = [],
    disabled = false,
    id,
    infiniteScroll = false,
    initialOptions = [],
    loadMoreData,
    maxTags = 3,
    onBlur,
    onChange,
    optionLabelProp = "title",
    optionValueProp = "value",
    placeholder,
    required = false,
    showAll = false,
    showIcons = true,
    showSearch = true,
    sort = true,
    title,
    value = [],
  } = props;

  const [state, setState] = useState(() => {
    return {
      count: 0,
      dropdownVisible: false,
      error: false,
      loading: false,
      pageNumber: 2,
      searchQuery: "",
      uuid: generateId(10, true),
    };
  });

  const { dropdownVisible, searchQuery = "", uuid } = state;

  const inputRef = useRef();
  const listRef = useRef();
  const stateRef = useRef({});
  stateRef.current = state;
  const reachedBottom = useRef(false);
  const ref = useRef({ inputRef, listRef, stateRef, reachedBottom });

  const enableSearch = infiniteScroll
    ? showSearch
    : showSearch &&
      passedData.length + (showAll ? 1 : 0) + initialOptions.length > 5;

  const dataSorter = sorter(optionLabelProp);

  const data = useMemo(
    () => {
      const allData = [];

      if (
        showAll &&
        !searchQuery &&
        (initialOptions.length || passedData.length)
      )
        allData.push({
          key: "all",
          [optionValueProp]: "all",
          [optionLabelProp]: "All",
        });

      if (initialOptions.length)
        !searchQuery
          ? allData.push(...initialOptions)
          : allData.push(
              ...filterOptions(initialOptions, searchQuery, optionValueProp)
            );

      if (infiniteScroll) allData.push(...passedData);
      else {
        const sortedData = sort ? passedData.sort(dataSorter) : passedData;

        if (searchQuery) {
          const filteredOptions = filterOptions(
            passedData,
            searchQuery,
            optionLabelProp
          );
          allData.push(...filteredOptions);
        } else {
          allData.push(...sortedData);
        }
      }

      return allData;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [passedData, searchQuery]
  );

  const itemClickHandler = (datum, checked) => () => {
    const isAll = getValueByKey(datum, optionValueProp) === "all";
    let selectedValues;
    if (checked)
      selectedValues = value.filter(
        (item) =>
          getValueByKey(item, optionValueProp) !==
          getValueByKey(datum, optionValueProp)
      );
    else
      selectedValues = isAll
        ? [{ [optionValueProp]: "all", [optionLabelProp]: "All" }]
        : [
            ...value.filter(
              (item) =>
                showAll ? getValueByKey(item, optionValueProp) !== "all" : true
            ),
            datum,
          ];

    onChange(selectedValues);
  };

  return (
    <>
      <CustomDropdown
        customClass={customClass}
        enableSearch={enableSearch}
        disabled={disabled}
        onBlur={onBlur}
        overlay={
          <DropdownMenu
            {...props}
            data={data}
            itemClickHandler={itemClickHandler}
            ref={ref}
            loadMoreData={loadMoreData}
            multiple
            setParentState={setState}
            parentState={state}
          />
        }
        dropdownVisible={dropdownVisible}
        id={id}
        required={required}
        setParentState={setState}
        title={title}
        uuid={uuid}
      >
        <div className="tags">
          <Tags
            disabled={disabled}
            maxTags={maxTags}
            onChange={onChange}
            optionLabelProp={optionLabelProp}
            optionValueProp={optionValueProp}
            value={value}
          />
          {!value?.length &&
            !enableSearch && (
              <span className="placeholder-text">{placeholder}</span>
            )}
          {enableSearch && (
            <SearchBox
              className="multiple"
              data={data}
              disabled={disabled}
              dropdownVisible={dropdownVisible}
              infiniteScroll={infiniteScroll}
              loadMoreData={loadMoreData}
              onBlur={onBlur}
              placeholder={value?.length ? "" : placeholder}
              ref={ref}
              value={searchQuery}
              setState={setState}
            />
          )}
        </div>
        {showIcons && (
          <MultipleSelectIcon
            allowClear={allowClear}
            defaultValue={defaultValue}
            disabled={disabled}
            dropdownVisible={dropdownVisible}
            onChange={onChange}
            optionValueProp={optionValueProp}
            setState={setState}
            showSearch={enableSearch}
            value={value}
          />
        )}
      </CustomDropdown>
    </>
  );
};

export default MultipleSelect;

export const MultipleFormikSelect = ({
  customClass,
  name,
  hideErrorMessage = false,
  onChange,
  ...props
}) => {
  const [{ value }, { touched, error }, { setValue, setTouched }] = useField(
    name
  );

  const handleChange = useCallback(
    (value) => {
      setValue(value);
      if (onChange) onChange(value);
    },
    [setValue, onChange]
  );

  return (
    <>
      <MultipleSelect
        {...props}
        onChange={handleChange}
        value={value}
        customClass={`${customClass} ${error && touched ? "error-box" : ""}`}
        onBlur={() => setTouched(true)}
      />
      {touched && error && !hideErrorMessage ? (
        <div className="error-label">{error}</div>
      ) : null}
    </>
  );
};
